import React, { useMemo, useState } from 'react';
import * as d3 from 'd3';
import { useDimensionsRef } from 'hooks/useDimensionsRef/useDimensionsRef';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';

const BAR_PADDING = 0.3;
const TOP_LABEL_HEIGHT = 24;
const SUB_LABEL_START = 16;
const SUB_LABEL_GAP = 14;
const ROTATED_LABEL_BOTTOM_MARGIN = 90;
const ROTATED_LABEL_LEFT_MARGIN = 60;
const AXIS_BOTTOM_PADDING = 10;
const SUB_LABEL_LINE_SPACING = 2;
const ABOVE_LABEL_OFFSET = 6;
const ABOVE_LABEL_GAP = 13;
const TICK_LINE_HEIGHT = 12;

export interface VerticalBarSubLabel {
  text: string;
  color: string;
}

export interface VerticalBarItem {
  key: string;
  value: number;
  ariaLabel: string;
  tooltipHtml: string;
  barColor: string;
  subLabels?: VerticalBarSubLabel[];
  aboveLabels?: VerticalBarSubLabel[];
}

interface VerticalBarChartProps {
  items: VerticalBarItem[];
  xTickFormat?: (key: string) => string;
  height?: number;
  ariaLabel: string;
  rotateSubLabels?: boolean;
}

export const VerticalBarChart: React.FC<VerticalBarChartProps> = ({
  items,
  xTickFormat,
  height = 300,
  ariaLabel,
  rotateSubLabels = false,
}) => {
  const [containerRef, { width: containerWidth }] = useDimensionsRef<HTMLDivElement>();
  const tooltip = useChartTooltip();
  const [hovered, setHovered] = useState<string | null>(null);

  const maxSubLabels = items.reduce((max, d) => Math.max(max, d.subLabels?.length || 0), 0);
  const maxTickLines = items.reduce((max, d) => {
    const label = xTickFormat ? xTickFormat(d.key) : d.key;
    return Math.max(max, label.split('\n').length);
  }, 1);
  const marginBottom = rotateSubLabels
    ? ROTATED_LABEL_BOTTOM_MARGIN
    : AXIS_BOTTOM_PADDING + maxSubLabels * (SUB_LABEL_GAP + SUB_LABEL_LINE_SPACING);
  const marginLeft = rotateSubLabels ? ROTATED_LABEL_LEFT_MARGIN : 20;
  const marginTop = TOP_LABEL_HEIGHT + 4 + (maxTickLines - 1) * TICK_LINE_HEIGHT;
  const margin = { top: marginTop, right: 20, bottom: marginBottom, left: marginLeft };

  const innerWidth = Math.max(containerWidth - margin.left - margin.right, 0);
  const innerHeight = height - margin.top - margin.bottom;

  const xScale = useMemo(
    () =>
      d3
        .scaleBand<string>()
        .domain(items.map((d) => d.key))
        .range([0, innerWidth])
        .padding(BAR_PADDING),
    [items, innerWidth],
  );

  const yScale = useMemo(() => {
    const maxValue = d3.max(items, (d) => d.value) || 1;
    const maxAboveLabels = items.reduce((max, d) => Math.max(max, d.aboveLabels?.length || 0), 0);
    const topReserve =
      maxAboveLabels > 0 ? maxAboveLabels * ABOVE_LABEL_GAP + ABOVE_LABEL_OFFSET + 4 : 0;
    const domainMax = maxAboveLabels > 0 ? maxValue : maxValue * 1.1;
    return d3.scaleLinear().domain([0, domainMax]).range([innerHeight, topReserve]);
  }, [items, innerHeight]);

  if (!containerWidth || items.length === 0) {
    return <div ref={containerRef} style={{ width: '100%', minHeight: height }} />;
  }

  return (
    <div ref={containerRef} style={{ width: '100%', minHeight: height }}>
      <svg width={containerWidth} height={height} aria-label={ariaLabel}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {items.map((d) => {
            const barX = xScale(d.key) || 0;
            const centerX = barX + xScale.bandwidth() / 2;
            return (
              <g key={d.key}>
                <text
                  x={centerX}
                  y={-8}
                  textAnchor="middle"
                  fontSize="12px"
                  fontWeight="500"
                  fill="#6D7682"
                >
                  {(xTickFormat ? xTickFormat(d.key) : d.key).split('\n').map((line, i, lines) => (
                    <tspan
                      key={i}
                      x={centerX}
                      dy={i === 0 ? -(lines.length - 1) * TICK_LINE_HEIGHT : TICK_LINE_HEIGHT}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
                <rect
                  x={barX}
                  y={yScale(d.value)}
                  width={xScale.bandwidth()}
                  height={innerHeight - yScale(d.value)}
                  fill={d.barColor}
                  rx={3}
                  role="img"
                  aria-label={d.ariaLabel}
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  opacity={hovered === d.key ? 0.8 : 1}
                  onMouseEnter={(e) => {
                    setHovered(d.key);
                    tooltip.show(e, d.tooltipHtml);
                  }}
                  onMouseMove={(e) => tooltip.move(e)}
                  onMouseLeave={() => {
                    setHovered(null);
                    tooltip.hide();
                  }}
                  onFocus={(e) => {
                    setHovered(d.key);
                    tooltip.show(e, d.tooltipHtml);
                  }}
                  onBlur={() => {
                    setHovered(null);
                    tooltip.hide();
                  }}
                />
                {d.aboveLabels?.map((above, i) => (
                  <text
                    key={`${d.key}-above-${i}`}
                    x={centerX}
                    y={yScale(d.value) - ABOVE_LABEL_OFFSET - i * ABOVE_LABEL_GAP}
                    textAnchor="middle"
                    fontSize="11px"
                    fontWeight="600"
                    fill={above.color}
                  >
                    {above.text}
                  </text>
                ))}
                {d.subLabels?.map((sub, i) => (
                  <text
                    key={`${d.key}-sub-${i}`}
                    x={centerX}
                    y={innerHeight + SUB_LABEL_START + i * SUB_LABEL_GAP}
                    textAnchor={rotateSubLabels ? 'end' : 'middle'}
                    fontSize="11px"
                    fontWeight={i === 0 ? '600' : '400'}
                    fill={sub.color}
                    transform={
                      rotateSubLabels
                        ? `rotate(-45, ${centerX}, ${
                            innerHeight + SUB_LABEL_START + i * SUB_LABEL_GAP
                          })`
                        : undefined
                    }
                  >
                    {sub.text}
                  </text>
                ))}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};
